import { useState } from 'react';
import { Columns3 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover.tsx';
import TableButtonMock from '@/components/buttons/TableButtonMock.tsx';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import {
  getColumnId,
  isVisibleColumn,
} from '@/components/table/preferences/columnPreferences.ts';
import {
  isPrimaryField,
  resolveFieldImportance,
} from '@/components/table/preferences/fieldImportance.ts';
import { getHeaderDisplayName } from '@/components/table/hooks/useColumns.tsx';
import { cn } from '@/lib/utils.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { toolbarPopoverClass } from '@/components/table/components/toolbarSurface.ts';

const ColumnVisibilityAction = () => {
  const [open, setOpen] = useState(false);
  const tableDefinition = useTableStoreContext((s) => s.tableDefinition);
  const columnVisibility = useTableStoreContext((s) => s.columnVisibility);
  const setColumnVisibility = useTableStoreContext(
    (s) => s.setColumnVisibility,
  );
  const resetColumnPreferences = useTableStoreContext(
    (s) => s.resetColumnPreferences,
  );
  const ready = useTableStoreContext((s) => s.columnPreferencesReady);

  const importance = resolveFieldImportance(tableDefinition.fields ?? []);
  const items = (tableDefinition.fields ?? [])
    .filter(isVisibleColumn)
    .map((field) => ({
      id: getColumnId(field),
      label: getHeaderDisplayName(field),
      togglable: (field.hideable ?? true) && !isPrimaryField(field, importance),
    }));

  const isColumnVisible = (id: string) => columnVisibility[id] !== false;
  const visibleCount = items.filter((item) => isColumnVisible(item.id)).length;

  const toggleColumn = (id: string, togglable: boolean) => {
    if (!togglable) return;
    const currentlyVisible = isColumnVisible(id);
    // at least one column must always stay visible
    if (currentlyVisible && visibleCount <= 1) return;
    setColumnVisibility((old) => ({ ...old, [id]: !currentlyVisible }));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <TableButtonMock disabled={!ready || items.length === 0}>
          <Columns3 />
          Kolumny
        </TableButtonMock>
      </PopoverTrigger>
      <PopoverContent align="end" className={cn('w-72', toolbarPopoverClass)}>
        <div className="flex items-center justify-between gap-2 pb-1 pl-2">
          <span className={columnLabelClass}>Widoczne kolumny</span>
          <ActionButton
            tone="ghost"
            size="toolbar"
            onClick={resetColumnPreferences}
          >
            Przywróć domyślne
          </ActionButton>
        </div>
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          {items.map((item) => {
            const checked = isColumnVisible(item.id);
            return (
              <label
                key={item.id}
                className={cn(
                  'flex items-center gap-2 rounded-sm px-2 py-1.5',
                  item.togglable
                    ? 'cursor-pointer hover:bg-neutral-50'
                    : 'cursor-not-allowed opacity-60',
                )}
              >
                <PrimitiveCheckbox
                  checked={checked}
                  disabled={!item.togglable}
                  onChange={() => toggleColumn(item.id, item.togglable)}
                  className="border-neutral-200"
                />
                <span className="truncate text-xs text-neutral-900">
                  {item.label}
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnVisibilityAction;
